import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { environmentCommon } from '../../../../environments/environment-common';
import { URLS } from '../../../data/navigation/navigation.data';
import { THEMES } from '../../../data/product/product.data';
import { isPaletteShown, THEME_PALETTES } from '../../../data/palette/palette.data';
import { RoutingService } from '../../../service/routing/routing.service';
import { AnalyticsService } from '../../../service/analytics/analytics.service';
import { UtilService } from '../../../service/util/util.service';

/** One platform column in the coverage matrix. Usually a top-level
 * marketplace, but Open VSX is split into its editor variants (Cursor,
 * Antigravity, …), each carries `matchName` = 'Open VSX' so a theme's single
 * Open VSX listing lights up every variant column. */
interface CoveragePlatform {
  name: string;
  logo: string;
  matchName: string;
  /** Short display label for the column header (e.g. "MacOS Terminal" → "MacOS")
   * so long names stay on one line. `name` is still used for the title + nav. */
  label: string;
  /** Column group the platform belongs to (IDEs, Terminals, …). */
  category: string;
}

/** A contiguous run of platform columns sharing a category, for the grouped
 * top header row (`span` = how many columns it covers). */
interface CoverageColumnGroup {
  category: string;
  span: number;
}

/** One theme/platform intersection cell. `done` → check, `planned` → "planned"
 * label, `none` → dash (the platform isn't offered for this theme).
 *
 * A `done` cell also carries the platform name its tick links to, which is the
 * COLUMN's name rather than the parent listing's — an Open VSX column is Cursor
 * or Antigravity, and those are the names the theme page matches `?platform=`
 * against. */
interface CoverageCell {
  state: 'done' | 'planned' | 'none';
  platformName?: string;
}

/** One theme row: identity stack + a cell per coverage platform. */
interface CoverageRow {
  key: string;
  /** palettes table: whether the theme has a listed palette (its row opens the palette, else the theme) */
  hasPalette?: boolean;
  name: string;
  logo: string;
  category: string;
  cells: CoverageCell[];
}

/** Themes grouped by their product line (Nature, Cities, …). */
interface CoverageGroup {
  category: string;
  rows: CoverageRow[];
}

/** One platform's accordion panel for the narrow-screen layout: the platform
 * itself plus the themes that ship (or are planned) on it. Replaces the wide
 * scrolling matrix on phones/tablets, pick a platform, see its themes. */
interface CoveragePlatformPanel {
  platform: CoveragePlatform;
  available: CoverageRow[];
  planned: CoverageRow[];
}

/**
 * Platform Coverage: every theme against every platform, a tick where it ships. It lived on the home
 * and moved to the foot of the themes page (owner 2026-10-11); wide screens get the table, narrow
 * ones a per-platform accordion. `kind="palettes"` (owner 2026-10-15, the foot of the palettes page):
 * every listed palette against the apps it comes for.
 */
@Component( {
  selector: 'app-coverage-matrix',
  templateUrl: './coverage-matrix.component.html',
  styleUrls: [ './coverage-matrix.component.scss' ],
} )
export class CoverageMatrixComponent implements OnInit, OnDestroy {
  /** What the table covers: themes on their platforms, or palettes in their apps. */
  @Input() kind: 'themes' | 'palettes' = 'themes';

  // Coverage matrix (themes × platforms). Columns are the top-level
  // marketplaces in the same order the themes-page Platform filter lists them,
  // except Open VSX is expanded into its editor variants (Cursor, Antigravity,
  // VS Codium). Rows are the themes grouped by product line, including the
  // All-in-One bundle under "Collections".
  // Ordered by column-group so each category's columns are contiguous:
  // IDEs → Terminals → Browsers → Communication.
  private static readonly COVERAGE_PLATFORM_KEYS = [
    'jetbrainsMarketplace', 'xcode', 'vscode', 'openVsx', 'zed', 'sublimeText', 'macos', 'iterm2', 'ghostty', 'claudeCode', 'codex', 'opencode', 'rhino', 'chrome', 'firefox', 'line', 'slack',
  ];
  // Shorter column-header labels for names that don't fit a single line.
  private static readonly COVERAGE_SHORT_LABELS: { [name: string]: string } = {
    'MacOS Terminal': 'MacOS',
    'Sublime Text': 'Sublime',
  };
  coveragePlatforms: CoveragePlatform[] = [];
  coverageColumnGroups: CoverageColumnGroup[] = [];
  coverageGroups: CoverageGroup[] = [];
  // Narrow-screen (<=999px) layout: the wide scrolling matrix is replaced by a
  // per-platform accordion. One panel per platform, listing the themes on it.
  // `coverageOpenPanel` is the index of the expanded panel (single-open); the
  // first platform opens by default so the section never reads as empty.
  coveragePlatformPanels: CoveragePlatformPanel[] = [];
  coverageOpenPanel: number | null = 0;
  // Crosshair hover: the column index the cursor is over (-1 = the theme name
  // column, 0..N = platform columns, null = none). The hovered ROW is handled
  // by CSS :hover; this drives the column highlight + the darker intersection.
  coverageHoverCol: number | null = null;
  // The category band is a sticky bar pinned to the left of the scroll viewport.
  // For `position: sticky` to actually pin, the bar must be NARROWER than its
  // colspan cell (which is the full table width), so we size it to the visible
  // scroll width and keep it in sync on resize.
  private coverageScrollEl?: HTMLElement;
  coverageBarWidth: number | null = null;
  @ViewChild( 'coverageScroll' ) set coverageScroll( ref: ElementRef<HTMLElement> | undefined ) {
    this.coverageScrollEl = ref?.nativeElement;
    // Deferred so setting the width doesn't fire inside the same change-detection
    // pass (would trip ExpressionChangedAfterItHasBeenChecked).
    setTimeout( () => this.refreshCoverageBarWidth() );
  }

  private refreshCoverageBarWidth(): void {
    const w = this.coverageScrollEl?.clientWidth ?? null;
    if ( w !== this.coverageBarWidth ) {
      this.coverageBarWidth = w;
    }
  }

  isSmallScreenCached = false;
  private resizeListener?: () => void;

  constructor( private routingService: RoutingService, private utilService: UtilService, private analyticsService: AnalyticsService ) {}

  ngOnInit(): void {
    if ( this.kind === 'palettes' ) {
      this.buildPaletteMatrix();
    } else {
      this.buildCoverageMatrix();
    }
    // cached, so the template's *ngIfs do not call the service on every check
    this.isSmallScreenCached = this.utilService.isSmallScreen();
    this.resizeListener = () => {
      this.isSmallScreenCached = this.utilService.isSmallScreen();
      this.refreshCoverageBarWidth();
    };
    window.addEventListener( 'resize', this.resizeListener, { passive: true } );
  }

  ngOnDestroy(): void {
    if ( this.resizeListener ) {
      window.removeEventListener( 'resize', this.resizeListener );
    }
  }

  /** Builds the platform columns + per-theme availability rows once. A theme
   * is "available" on a platform when it carries a matching `platforms` entry
   * (matched on the top-level platform name, so Open VSX stays one column). */
  private buildCoverageMatrix(): void {
    const website: any = environmentCommon.website;
    const labels = CoverageMatrixComponent.COVERAGE_SHORT_LABELS;
    this.coveragePlatforms = CoverageMatrixComponent.COVERAGE_PLATFORM_KEYS
      .map( key => website[ key ] )
      .filter( w => w?.name )
      .map( w => {
        // Category lives on the platform definition (WebsiteModel.category).
        const category = w.category ?? '';
        // Open VSX (and any platform carrying editor variants) expands into one
        // column per variant; all variants resolve against the parent listing
        // and inherit its category.
        if ( w.supportedPlatforms?.length > 0 ) {
          return w.supportedPlatforms.map( ( sp: any ) =>
            ( { name: sp.name, logo: sp.logo, matchName: w.name, label: labels[ sp.name ] ?? sp.name, category: sp.category ?? category } ) );
        }
        return [ { name: w.name, logo: w.logo, matchName: w.name, label: labels[ w.name ] ?? w.name, category } ];
      } )
      .reduce( ( acc, val ) => acc.concat( val ), [] as CoveragePlatform[] );

    // Collapse the ordered columns into contiguous category runs for the grouped
    // top header row.
    this.coverageColumnGroups = [];
    for ( const p of this.coveragePlatforms ) {
      const last = this.coverageColumnGroups[ this.coverageColumnGroups.length - 1 ];
      if ( last && last.category === p.category ) {
        last.span++;
      } else {
        this.coverageColumnGroups.push( { category: p.category, span: 1 } );
      }
    }

    this.coverageGroups = THEMES
      .map( line => ( {
        category: line.name,
        rows: ( line.products ?? [] )
          .filter( p => ( p.platforms?.length ?? 0 ) > 0 )
          .map( p => ( {
            key: p.key,
            name: p.name,
            logo: p.logo,
            category: line.name,
            cells: this.coveragePlatforms.map( plat => {
              const entry = p.platforms?.find( pl => pl.info?.name === plat.matchName );
              const state: CoverageCell[ 'state' ] = !entry
                ? 'none'
                : ( entry.status === 'planned' ? 'planned' : 'done' );
              return state === 'done' ? { state, platformName: plat.name } : { state };
            } ),
          } ) ),
      } ) )
      .filter( group => group.rows.length > 0 );

    this.buildPanels();
  }

  /** The palettes' table: every theme by product line, a column per app (Figma, CorelDRAW, the Mac's
   *  color picker), a tick where its listed palette (palette.data.ts, pictures in) comes for it. */
  private buildPaletteMatrix(): void {
    const palettes = THEME_PALETTES.filter( pl => isPaletteShown( pl.key ) );
    const apps = new Map<string, { name: string; logo: string }>();
    palettes.forEach( pl => pl.platforms.forEach( app => apps.has( app.key ) || apps.set( app.key, app ) ) );
    this.coveragePlatforms = [ ...apps.values() ].map( app => ( {
      name: app.name,
      logo: app.logo,
      matchName: app.name,
      // "Figma", "CorelDRAW", "MacOS", as the palettes filter names them
      label: app.name.replace( / Color Palette$| Palette$/, '' ),
      category: 'Apps',
    } ) );
    this.coverageColumnGroups = [ { category: 'Apps', span: this.coveragePlatforms.length } ];
    // every theme, as the themes table lists them (owner 2026-10-15), a dash where it has no palette yet
    this.coverageGroups = THEMES
      .map( line => ( {
        category: line.name,
        rows: ( line.products ?? [] )
          .filter( p => ( p.platforms?.length ?? 0 ) > 0 )
          .map( p => {
            const palette = palettes.find( pl => pl.key === p.key );
            return {
              key: p.key,
              name: p.name,
              logo: p.logo,
              category: line.name,
              hasPalette: !!palette,
              cells: this.coveragePlatforms.map( app => palette?.platforms.some( pl => pl.name === app.name )
                ? { state: 'done' as const, platformName: app.name }
                : { state: 'none' as const } ),
            };
          } ),
      } ) )
      .filter( group => group.rows.length > 0 );
    this.buildPanels();
  }

  private buildPanels(): void {
    // Transpose the matrix into per-platform panels for the narrow-screen
    // accordion: for each platform column, collect the themes whose cell at that
    // column is `done` (available) or `planned`.
    const allRows = this.coverageGroups
      .reduce( ( acc, g ) => acc.concat( g.rows ), [] as CoverageRow[] );
    this.coveragePlatformPanels = this.coveragePlatforms.map( ( plat, i ) => ( {
      platform: plat,
      available: allRows.filter( r => r.cells[ i ]?.state === 'done' ),
      planned: allRows.filter( r => r.cells[ i ]?.state === 'planned' ),
    } ) );
  }

  /** Expand/collapse a platform panel in the narrow-screen accordion (single
   * panel open at a time; tapping the open one collapses it). */
  toggleCoveragePanel( index: number ): void {
    this.coverageOpenPanel = this.coverageOpenPanel === index ? null : index;
  }

  /** Open a theme's detail page from its coverage-matrix row. */
  goToCoverageTheme( row: CoverageRow ): void {
    this.analyticsService.logEvent( 'Coverage Theme Clicked', { table: this.kind, theme: row.key, category: row.category } );
    if ( this.kind === 'palettes' && row.hasPalette ) {
      this.routingService.redirect( URLS.colorPalette, { product: row.key, category: row.category.toLowerCase() } );
      return;
    }
    this.routingService.redirect( URLS.theme, { product: row.key, category: row.category } );
  }

  /**
   * Open a ticked coverage cell: the THEME's own page, with that platform
   * already selected (owner 2026-08-13).
   *
   * Not the marketplace listing, which is where an earlier pass sent it: the
   * matrix is a way into the site's own theme pages, and the theme page shows
   * that platform's screenshot, its install guide and its CTA, so the visitor
   * can see what they are getting before leaving for a store. The theme page
   * already honours `?platform=` — it pins the carousel to that platform and
   * stops auto-cycling — so this is the parameter it was built for.
   *
   * No click is recorded here, deliberately. `recordClick` counts traffic sent
   * OUT to a marketplace; this navigation stays on the site, and the theme
   * page records it if the visitor goes on to click through.
   *
   * Only `done` cells are wired. `planned` has nothing to show yet, and `none`
   * is the absence of a thing.
   */
  goToCoverageCell( row: CoverageRow, index: number ): void {
    const cell = row.cells[ index ];
    // every cell click (owner 2026-10-15), the empty and planned ones too: what visitors look for
    this.analyticsService.logEvent( 'Coverage Cell Clicked', {
      table: this.kind, theme: row.key, category: row.category, platform: cell?.platformName || this.coveragePlatforms[ index ]?.name, state: cell?.state || 'none',
    } );
    if ( !cell || cell.state !== 'done' ) {
      return;
    }
    if ( this.kind === 'palettes' ) {
      this.routingService.redirect( URLS.colorPalette, { product: row.key, category: row.category.toLowerCase(), platform: cell.platformName } );
      return;
    }
    this.routingService.redirect( URLS.theme, {
      product: row.key,
      category: row.category,
      platform: cell.platformName,
    } );
  }

  /** Open the themes gallery pre-filtered to the clicked platform column. The
   * themes page reads the `platform` query param (case-insensitive) and matches
   * it against its platform filter options, including the Open VSX editor
   * variants (Cursor, Antigravity, …), so every column header deep-links. */
  goToThemesByPlatform( platformName: string ): void {
    if ( !platformName ) {
      return;
    }
    this.analyticsService.logEvent( 'Coverage Platform Clicked', { table: this.kind, platform: platformName } );
    this.routingService.redirect( this.kind === 'palettes' ? URLS.palettes : URLS.themes, { platform: platformName } );
  }
}
